import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConfig } from '../config';
import { useAuth } from '../auth/AuthContext';

declare global {
  interface Window {
    google: any;
    AppleID: any;
  }
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { setUserForSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const { apiBase, googleClientId, appleServiceId, appleRedirectUrl } = getConfig();
  const googleBtnRef = useRef<HTMLDivElement | null>(null);

  const signInViaBackend = useCallback(async (url: string, body: any) => {
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const msg = `${errData?.message || 'Sign-in failed'}`.toLowerCase();
        if (msg.includes('no account')) {
          setError('You must have an account to sign in.');
        } else if (msg.includes('superadmin')) {
          setError('Your account does not have super admin permissions.');
        } else {
          setError('Sign-in failed. Please try again.');
        }
        return;
      }
      const data = await res.json();
      if (!data?.user) {
        setError('Sign-in failed. No user returned.');
        return;
      }
      // Persist user for immediate guarded-route access
      setUserForSession(data.user);
      // Dismiss any One Tap UI
      if (window.google?.accounts?.id?.cancel) {
        window.google.accounts.id.cancel();
      }
      navigate('/');
    } catch (e) {
      console.error(e);
      setError('Network error. Please try again.');
    }
  }, [navigate, setUserForSession]);

  const handleGoogleResponse = useCallback(async (response: any) => {
    const idToken = response?.credential;
    if (!idToken) return;
    await signInViaBackend(`${apiBase}/auth/google-superadmin`, { token: idToken });
  }, [apiBase, signInViaBackend]);

  useEffect(() => {
    if (!window.google?.accounts?.id) return;
    // Avoid One Tap auto-select reusing prior choice without visible UI
    if (window.google?.accounts?.id?.disableAutoSelect) {
      window.google.accounts.id.disableAutoSelect();
    }
    window.google.accounts.id.initialize({
      client_id: googleClientId,
      callback: handleGoogleResponse,
    });
    // Render a visible Google button to avoid off-screen One Tap UX issues
    if (googleBtnRef.current && window.google?.accounts?.id?.renderButton) {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
      });
    }
  }, [googleClientId, handleGoogleResponse]);

  useEffect(() => {
    if (!window.AppleID?.auth) return;
    window.AppleID.auth.init({
      clientId: appleServiceId,
      scope: 'name email',
      redirectURI: appleRedirectUrl,
      usePopup: true,
    });
  }, [appleServiceId, appleRedirectUrl]);

  

  // No One Tap prompt – we only use the standard rendered Google button

  const handleAppleClick = async () => {
    setError(null);
    try {
      const appleResp = await window.AppleID.auth.signIn();
      const idToken = appleResp?.authorization?.id_token;
      const name = appleResp?.user?.name;
      const email = appleResp?.user?.email;
      const body: any = { token: idToken };
      if (email) body.email = email;
      if (name) { body.firstName = name.firstName; body.lastName = name.lastName; }
      await signInViaBackend(`${apiBase}/auth/apple-superadmin`, body);
    } catch (err: any) {
      if (err?.error === 'popup_closed_by_user') return;
      console.error('Apple Sign-In error', err);
      setError('Apple Sign-In failed. Please try again.');
    }
  };

  

  return (
    <main className="login-page p-8 max-w-md mx-auto">
      <h1 className="text-2xl font-semibold mb-4">SuperAdmin Portal Login</h1>
      {error && <p className="text-red-600 mb-4">{error}</p>}
      <div className="flex flex-col gap-4">
        <div ref={googleBtnRef} id="google-btn-container" />
        <button className="px-4 py-2 bg-black text-white rounded" onClick={handleAppleClick}>Sign in with Apple</button>
      </div>
    </main>
  );
};

export default LoginPage;

